import React, { createContext, Fragment } from 'react';
import { Dialog as HDialog, Transition } from '@headlessui/react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import tw from 'twin.macro';

// Besoin de ça pour safari...
import 'intersection-observer';
import { XMarkIcon as X } from '@heroicons/react/24/outline';

type Size = '2xl' | 'xl' | 'lg' | 'md' | 'sm';

type ZIndex = '10' | '20' | '30' | '40' | '50';

interface Props {
  children: React.ReactNode;
  disableMaxHeight?: boolean;
  isOpen: boolean;
  size?: Size;
  className?: string;
  onClose: () => void;
  title?: string;
  closeButton?: boolean;
  full?: boolean;
  initialFocus?: any;
  disabledClickOutside?: boolean;
  zIndex?: ZIndex;
  position?: 'center' | 'top';
}

const DialogContext = createContext<{ size?: Size }>({
  size: 'lg',
});

const headerFooterHeight = '9rem';
const headerFooterHeightWithSubheader = '12rem';

const modalSizes = {
  sm: tw`max-w-xs`,
  md: tw`max-w-lg`,
  lg: tw`max-w-2xl`,
  xl: tw`max-w-6xl`,
  '2xl': tw`max-w-7xl`,
};

const ResponsiveDialog = styled.div(
  ({
    size,
    full,
    position,
    disableMaxHeight,
  }: {
    size: Size;
    full: boolean;
    position: 'top' | 'center';
    disableMaxHeight?: boolean;
  }) => [
    modalSizes[size],
    tw`inline-block w-full overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl relative`,
    !full && tw`rounded-lg`,
    position === 'top' && tw`md:mt-10`,
    !disableMaxHeight &&
      css`
        max-height: 90vh;
        @media (max-width: 600px) {
          max-height: 100vh;
          max-height: 100dvh;
        }
      `,
  ],
);

const ResponsiveBody = styled.div<{
  withSubheader?: boolean;
}>`
  max-height: calc(
    90vh -
      ${props =>
        props.withSubheader
          ? headerFooterHeightWithSubheader
          : headerFooterHeight}
  );
  @media (max-width: 600px) {
    max-height: calc(
      100vh -
        ${props =>
          props.withSubheader
            ? headerFooterHeightWithSubheader
            : headerFooterHeight}
    );
    max-height: calc(
      100dvh -
        ${props =>
          props.withSubheader
            ? headerFooterHeightWithSubheader
            : headerFooterHeight}
    );
  }
`;

interface HeaderProps {
  title?: string;
  children?: React.ReactNode;
}

function Header({ children, title }: HeaderProps) {
  return (
    <HDialog.Title as="h3" tw="text-lg font-medium leading-6 px-4 border-b">
      {title ? <div tw="text-lg font-bold py-3">{title}</div> : children}
    </HDialog.Title>
  );
}

export const getZIndex: Record<ZIndex, any> = {
  '50': tw`z-50`,
  '40': tw`z-40`,
  '30': tw`z-30`,
  '20': tw`z-20`,
  '10': tw`z-10`,
};

export default function Dialog(props: Props) {
  const {
    isOpen = true,
    onClose,
    children,
    position = 'center',
    className = '',
    zIndex = '20',
    title,
    full = false,
    size = 'lg',
    disabledClickOutside = true,
    disableMaxHeight = false,
    closeButton = false,
    initialFocus,
  } = props;
  return (
    //@ts-ignore
    <Transition show={isOpen} as={Fragment}>
      <HDialog
        as="div"
        open={isOpen}
        initialFocus={initialFocus}
        onClose={() => !disabledClickOutside && onClose()}
        className={className}
      >
        <div
          css={[
            css`
              min-height: 100vh;
              min-height: calc(var(--vh, 1vh) * 100);
            `,
            tw`fixed top-0 left-0 right-0 bottom-0 overflow-y-auto text-center`,
            !full && tw`px-4`,
            getZIndex[zIndex],
          ]}
        >
          {/*@ts-ignore*/}
          <Transition.Child
            as={Fragment as any}
            enter="overlayEnter"
            enterFrom="overlayEnterFrom"
            enterTo="overlayEnterTo"
            leave="overlayLeave"
            leaveFrom="overlayLeaveFrom"
            leaveTo="overlayLeaveTo"
          >
            <HDialog.Overlay tw="fixed top-0 right-0 bottom-0 left-0 bg-neutral-intense bg-opacity-70" />
          </Transition.Child>
          {/* This element is to trick the browser into centering the modal contents. */}
          {position === 'center' && (
            <span
              css={[
                tw`hidden md:inline-block h-screen`,
                position === 'center' && tw`align-middle`,
              ]}
              aria-hidden="true"
            >
              &#8203;
            </span>
          )}
          {isOpen && (
            <Transition.Child
              as={Fragment as any}
              enter="dialogEnter"
              enterFrom="dialogEnterFrom"
              enterTo="dialogEnterTo"
              leave="dialogLeave"
              leaveFrom="dialogLeaveFrom"
              leaveTo="dialogLeaveTo"
            >
              <ResponsiveDialog
                data-testid="dialog"
                full={full}
                size={size}
                position={position}
                disableMaxHeight={disableMaxHeight}
              >
                {closeButton && (
                  <div tw="block absolute top-0 right-0 pt-4 pr-4 z-50">
                    <button
                      type="button"
                      tw="bg-transparent rounded-md text-neutral-quiet hover:text-neutral-quiet"
                      onClick={() => onClose()}
                    >
                      <span tw="sr-only">Close</span>
                      <X tw="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                )}
                {title && <Header title={title} />}
                <DialogContext.Provider value={{ size }}>
                  {children}
                </DialogContext.Provider>
              </ResponsiveDialog>
            </Transition.Child>
          )}
        </div>
      </HDialog>
    </Transition>
  );
}

interface BodyProps {
  withSubheader?: boolean;
  fullHeight?: boolean;
  children: React.ReactNode;
  className?: string;
}

function Body({ children, withSubheader, className, fullHeight }: BodyProps) {
  return (
    <ResponsiveBody
      tw="overflow-auto p-4 bg-neutral-supershy"
      withSubheader={withSubheader}
      className={className}
      css={[
        fullHeight &&
          css`
            max-height: calc(var(--vh, 1vh) * 100 - 4rem) !important;
          `,
      ]}
    >
      {children}
    </ResponsiveBody>
  );
}

function Footer({ children, className }: any) {
  return (
    <div
      data-testid="footer"
      tw="border p-4 bg-neutral-supershy"
      className={className}
    >
      <div tw="flex justify-end items-center gap-2.5">{children}</div>
    </div>
  );
}

Dialog.Header = Header;
Dialog.Body = Body;
Dialog.Footer = Footer;
